import { useMutation } from '@tanstack/react-query';

import { logSentryError, useCart } from '@libs/common-next';
import type { AddItemRequestBody, CartUI } from '@libs/common-node';

import { SentryErrors } from '@/shared/sentry';

export type UseAddToCartOnSuccess = {
    cart: CartUI;
    payload: AddItemRequestBody[];
    isProductRecommendation?: boolean;
};

type UseAddToCartProp = {
    onError?: (error: Error) => void;
    onSuccess?: (props: UseAddToCartOnSuccess) => void;
    isProductRecommendation?: boolean;
};

type CartErrorResponse = CartUI & {
    detail: string;
    title: string;
    status: number;
    instance: string;
    invalid_fields?: Record<string, string>;
};

const isValidCartUI = (data: CartUI): boolean =>
    Boolean(data.id && data.basketId && Array.isArray(data.fulfillmentGroups));

export const useAddToCart = ({ onSuccess, onError, isProductRecommendation }: UseAddToCartProp) => {
    const { addItems } = useCart();

    const handleError = (error: Error) => {
        logSentryError(error, SentryErrors.AddToCartAPI.AddToCartFailed);
        onError?.(error);
    };

    return useMutation({
        mutationFn: addItems,
        onSuccess: (data, payload) => {
            if (!data) {
                const error = new Error('No data returned from cart.');
                return handleError(error);
            }
            if (data.status && data.status >= 400) {
                const error = new Error((data as CartErrorResponse).detail);
                return handleError(error);
            }
            if (!isValidCartUI(data)) {
                const error = new Error('Malformed cart response: missing required fields.');
                return handleError(error);
            }
            return onSuccess?.({
                cart: data,
                payload,
                isProductRecommendation,
            });
        },
        onError: handleError,
    });
};
